import { useEffect } from "react";
import { motion } from "framer-motion";
import { EASE_OUT_EXPO, EASE_IN_OUT_SMOOTH } from "../../lib/motion";

const HOLD_MS = 1700;

interface LoginIntroProps {
  appName: string;
  logoSrc: string;
  onComplete: () => void;
}

/**
 * Full-screen brand intro shown once per session before the login reveals.
 * Logo scales/rotates/fades in, the app name staggers letter-by-letter, then
 * the overlay auto-dismisses. Click or Escape skips it immediately.
 */
export function LoginIntro({ appName, logoSrc, onComplete }: LoginIntroProps) {
  const name = appName || "Claims App";
  const letters = name.split("");

  useEffect(() => {
    const timer = setTimeout(onComplete, HOLD_MS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") onComplete();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
    };
  }, [onComplete]);

  return (
    <motion.div
      className="login-intro"
      onClick={onComplete}
      role="button"
      tabIndex={-1}
      aria-label={`${name}, tap to continue`}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.55, ease: EASE_IN_OUT_SMOOTH }}
    >
      <div className="login-intro-glow" aria-hidden="true" />

      <motion.div
        className="login-intro-badge"
        initial={{ scale: 0.4, opacity: 0, rotate: -8 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{
          duration: 0.9,
          ease: EASE_OUT_EXPO,
          opacity: { duration: 0.6 },
        }}
      >
        <img src={logoSrc} alt="" />
      </motion.div>

      <div className="login-intro-name" aria-label={name}>
        {letters.map((char, i) => (
          <motion.span
            key={i}
            aria-hidden="true"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.5 + i * 0.05,
              duration: 0.5,
              ease: EASE_OUT_EXPO,
            }}
          >
            {char === " " ? " " : char}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
}
